// =============================================
// transferRunner — wykonuje TransferPlan SEKWENCYJNIE (domena po domenie,
// w domenie: zone_create → ns_set → a_record). Deps wstrzykiwane (testy bez
// sieci). NIGDY rownolegle — zmiana NS to operacja nieodwracalna w skutkach.
//
//  * skip z planu → status "skipped" + log skipped z powodem (Historia pokaze dlaczego)
//  * zone_create failed → ns_set(from_zone) i a_record "skipped: zona nie powstala"
//  * wiersz z >= 1 failed → consecutive++, inaczej reset; >= max (3) → stop,
//    reszta zostaje "pending"
//  * isStopRequested sprawdzane TYLKO miedzy domenami
//  * onlyKeys (Ponow) + alreadyDone: akcje juz "ok" → skip "juz wykonane"
//  * log = best-effort (insertDnsChangeLog nie rzuca)
// =============================================
import type { CfZone, PointDomainResult } from "@/services/cloudflareService";
import type { AmWorkspace } from "@/services/aftermarketService";
import type { DnsChangeLogInsert } from "./dnsChangeLogService";
import type { PlanRow, RowActionStatus, RowRunState, TransferPlan, TransferSummary } from "./transferTypes";

export interface TransferRunnerDeps {
  addZone: (token: string, domain: string, accountId: string) => Promise<CfZone>;
  nsSet: (name: string, ns: string[], workspace: AmWorkspace) => Promise<boolean>;
  pointDomainToIp: (token: string, zoneId: string, zoneName: string, ip: string, proxied: boolean) => Promise<PointDomainResult>;
  log: (row: DnsChangeLogInsert) => Promise<void>;
  /** token konta CF po cloudflare_accounts.id */
  getToken: (cfAccountId: string) => string | undefined;
  /** CF account_id (32 hex) po cloudflare_accounts.id — do addZone */
  getAccountId: (cfAccountId: string) => string | undefined;
}

export interface TransferControl {
  isStopRequested: () => boolean;
}

export interface TransferRunnerOptions {
  /** domyslnie 3 */
  maxConsecutiveFailures?: number;
  /** "Ponow": tylko te klucze */
  onlyKeys?: string[];
  /** poprzedni stan wierszy — akcje "ok" nie sa powtarzane */
  alreadyDone?: Record<string, RowRunState>;
}

const AUTH_ERR_RE = /authentication|10000|permission|not authorized|unauthorized/i;

export const DEFAULT_MAX_CONSECUTIVE_FAILURES = 3;

export function emptyRowState(key: string): RowRunState {
  return {
    key,
    zone_create: { status: "pending" },
    ns_set: { status: "pending" },
    a_record: { status: "pending" },
    domainFailed: false,
  };
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const withHint = (msg: string) =>
  AUTH_ERR_RE.test(msg) ? `${msg} — token może nie mieć DNS:Edit / Zone:Edit (Konta → Sprawdź token)` : msg;

export async function runTransfer(
  plan: TransferPlan,
  deps: TransferRunnerDeps,
  control: TransferControl,
  onRow: (state: RowRunState) => void,
  opts: TransferRunnerOptions = {},
): Promise<TransferSummary> {
  const max = opts.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;
  const only = opts.onlyKeys ? new Set(opts.onlyKeys) : null;
  const rows = only ? plan.rows.filter((r) => only.has(r.domain.key)) : plan.rows;

  const summary: TransferSummary = { total: rows.length, ok: 0, failed: 0, skipped: 0, stoppedReason: null };
  let consecutive = 0;

  for (const row of rows) {
    if (control.isStopRequested()) { summary.stoppedReason = "user"; break; }
    if (consecutive >= max) { summary.stoppedReason = "consecutive_failures"; break; }

    const state = await runRow(row, deps, onRow, opts.alreadyDone?.[row.domain.key]);
    const statuses: RowActionStatus[] = [state.zone_create.status, state.ns_set.status, state.a_record.status];
    const anyFailed = statuses.includes("failed");
    const allSkipped = statuses.every((s) => s === "skipped");

    if (anyFailed) { summary.failed++; consecutive++; }
    else if (allSkipped) { summary.skipped++; }
    else { summary.ok++; consecutive = 0; }
  }

  return summary;
}

async function runRow(
  row: PlanRow,
  deps: TransferRunnerDeps,
  onRow: (state: RowRunState) => void,
  previous: RowRunState | undefined,
): Promise<RowRunState> {
  const d = row.domain;
  const key = d.key;
  const [zoneAct, nsAct, aAct] = row.actions;
  const state: RowRunState = previous ? structuredCloneSafe(previous) : emptyRowState(key);
  state.domainFailed = false;
  const emit = () => onRow({ ...state, zone_create: { ...state.zone_create }, ns_set: { ...state.ns_set }, a_record: { ...state.a_record } });

  const baseLog = (action: DnsChangeLogInsert["action"], cfAccountId: string | null) =>
    ({ website_id: d.websiteId, domain: key, action, cf_account_id: cfAccountId, workspace: d.workspace });

  // Zona do dalszych krokow: istniejaca albo swiezo utworzona.
  let zone: CfZone | null = row.existingZone;
  let zoneCfAccountId: string | null = row.tokenCfAccountId;

  // ---------------- zone_create ----------------
  if (previous?.zone_create.status === "ok") {
    state.zone_create = { ...previous.zone_create, status: "skipped", message: "już wykonane" };
    zone = previous.zone_create.zone ?? zone;
  } else if (zoneAct.kind === "zone_create" && zoneAct.decision === "skip") {
    state.zone_create = { status: "skipped", message: zoneAct.reason };
    await deps.log({ ...baseLog("zone_create", zoneCfAccountId), before: null, after: null, status: "skipped", error: zoneAct.reason });
  } else if (zoneAct.kind === "zone_create") {
    state.zone_create = { status: "running" }; emit();
    const token = deps.getToken(zoneAct.cfAccountId);
    const accountId = deps.getAccountId(zoneAct.cfAccountId);
    zoneCfAccountId = zoneAct.cfAccountId;
    if (!token || !accountId) {
      const msg = !token ? "brak tokena konta Cloudflare" : "konto bez Account ID";
      state.zone_create = { status: "failed", message: msg };
      state.domainFailed = true;
      await deps.log({ ...baseLog("zone_create", zoneCfAccountId), before: null, after: null, status: "failed", error: msg });
    } else {
      try {
        const created = await deps.addZone(token, key, accountId);
        zone = created;
        state.zone_create = { status: "ok", zone: created, message: `${created.status}${created.name_servers?.length ? ` · ${created.name_servers.join(", ")}` : ""}` };
        await deps.log({ ...baseLog("zone_create", zoneCfAccountId), before: null, after: { zone_id: created.id, status: created.status, name_servers: created.name_servers ?? [] }, status: "success", error: null });
      } catch (e) {
        const msg = withHint(errMsg(e));
        state.zone_create = { status: "failed", message: msg };
        state.domainFailed = true;
        await deps.log({ ...baseLog("zone_create", zoneCfAccountId), before: null, after: null, status: "failed", error: msg });
      }
    }
  }
  emit();

  // ---------------- ns_set ----------------
  if (previous?.ns_set.status === "ok") {
    state.ns_set = { ...previous.ns_set, status: "skipped", message: "już wykonane" };
  } else if (nsAct.kind === "ns_set" && nsAct.decision === "skip") {
    state.ns_set = { status: "skipped", message: nsAct.reason };
    await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: null, after: nsAct.manualNs && nsAct.manualNs !== "FROM_CREATED_ZONE" ? { manual_ns: nsAct.manualNs } : null, status: "skipped", error: nsAct.reason });
  } else if (nsAct.kind === "ns_set") {
    const targetNs = nsAct.ns === "FROM_CREATED_ZONE" ? (zone?.name_servers ?? []) : nsAct.ns;
    if (nsAct.ns === "FROM_CREATED_ZONE" && state.zone_create.status === "failed") {
      state.ns_set = { status: "skipped", message: "zona nie powstała — brak NS" };
      await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: { ns: nsAct.currentNs }, after: null, status: "skipped", error: "zona nie powstała" });
    } else if (targetNs.length < 2) {
      const msg = "Cloudflare nie zwrócił nameserverów — odśwież zony i użyj Domeny w CF";
      state.ns_set = { status: "failed", message: msg };
      state.domainFailed = true;
      await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: { ns: nsAct.currentNs }, after: null, status: "failed", error: msg });
    } else {
      state.ns_set = { status: "running", before: nsAct.currentNs, after: targetNs }; emit();
      try {
        const ok = await deps.nsSet(key, targetNs, nsAct.workspace);
        if (ok) {
          state.ns_set = { status: "ok", before: nsAct.currentNs, after: targetNs, message: targetNs.join(", ") };
          await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: { ns: nsAct.currentNs }, after: { ns: targetNs }, status: "success", error: null });
        } else {
          const msg = "AfterMarket nie potwierdził zmiany";
          state.ns_set = { status: "failed", before: nsAct.currentNs, after: targetNs, message: msg };
          state.domainFailed = true;
          await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: { ns: nsAct.currentNs }, after: { ns: targetNs }, status: "failed", error: msg });
        }
      } catch (e) {
        const msg = errMsg(e);
        state.ns_set = { status: "failed", before: nsAct.currentNs, after: targetNs, message: msg };
        state.domainFailed = true;
        await deps.log({ ...baseLog("ns_set", zoneCfAccountId), before: { ns: nsAct.currentNs }, after: { ns: targetNs }, status: "failed", error: msg });
      }
    }
  }
  emit();

  // ---------------- a_record ----------------
  if (previous?.a_record.status === "ok") {
    state.a_record = { ...previous.a_record, status: "skipped", message: "już wykonane" };
  } else if (aAct.kind === "a_record" && aAct.decision === "skip") {
    state.a_record = { status: "skipped", message: aAct.reason };
    await deps.log({ ...baseLog("a_record", zoneCfAccountId), before: null, after: null, status: "skipped", error: aAct.reason });
  } else if (aAct.kind === "a_record") {
    if (!zone) {
      const msg = state.zone_create.status === "failed" ? "zona nie powstała — brak miejsca na rekordy" : "brak zony";
      state.a_record = { status: "skipped", message: msg };
      await deps.log({ ...baseLog("a_record", zoneCfAccountId), before: null, after: { ip: aAct.ip, proxied: aAct.proxied }, status: "skipped", error: msg });
    } else {
      const token = zoneCfAccountId ? deps.getToken(zoneCfAccountId) : undefined;
      if (!token) {
        const msg = "brak tokena konta Cloudflare";
        state.a_record = { status: "failed", message: msg };
        state.domainFailed = true;
        await deps.log({ ...baseLog("a_record", zoneCfAccountId), before: null, after: { ip: aAct.ip, proxied: aAct.proxied }, status: "failed", error: msg });
      } else {
        state.a_record = { status: "running" }; emit();
        try {
          const res = await deps.pointDomainToIp(token, zone.id, zone.name, aAct.ip, aAct.proxied);
          state.a_record = { status: "ok", result: res, message: `@ ${res.root.action}, www ${res.www.action} → ${aAct.ip}` };
          await deps.log({ ...baseLog("a_record", zoneCfAccountId), before: null, after: { ip: aAct.ip, proxied: aAct.proxied, root: res.root.action, www: res.www.action }, status: "success", error: null });
        } catch (e) {
          const msg = withHint(errMsg(e));
          state.a_record = { status: "failed", message: msg };
          state.domainFailed = true;
          await deps.log({ ...baseLog("a_record", zoneCfAccountId), before: null, after: { ip: aAct.ip, proxied: aAct.proxied }, status: "failed", error: msg });
        }
      }
    }
  }
  emit();

  return state;
}

function structuredCloneSafe(s: RowRunState): RowRunState {
  return {
    key: s.key,
    zone_create: { ...s.zone_create },
    ns_set: { ...s.ns_set, before: s.ns_set.before ? [...s.ns_set.before] : undefined, after: s.ns_set.after ? [...s.ns_set.after] : undefined },
    a_record: { ...s.a_record },
    domainFailed: s.domainFailed,
  };
}
